import { isbot } from "isbot";

export interface BrowserEnvironment {
  userAgent: string;
  webdriver: boolean;
  hasChromeRuntime: boolean;
  hasPermissionsApi: boolean;
  languages: readonly string[] | null;
  hasConnectionApi: boolean;
  hasOuterHeight: boolean;
  hasOuterWidth: boolean;
  hasInnerHeight: boolean;
  hasInnerWidth: boolean;
}

export function readBrowserEnvironment(): BrowserEnvironment | null {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return null;
  }

  return {
    userAgent: navigator.userAgent,
    webdriver: navigator.webdriver === true,
    hasChromeRuntime:
      typeof (window as Window & { chrome?: unknown }).chrome !== "undefined",
    hasPermissionsApi: typeof navigator.permissions !== "undefined",
    languages: Array.isArray(navigator.languages) ? navigator.languages : null,
    hasConnectionApi:
      typeof (navigator as Navigator & { connection?: unknown }).connection !==
      "undefined",
    hasOuterHeight: typeof window.outerHeight !== "undefined",
    hasOuterWidth: typeof window.outerWidth !== "undefined",
    hasInnerHeight: typeof window.innerHeight !== "undefined",
    hasInnerWidth: typeof window.innerWidth !== "undefined",
  };
}

/**
 * Conservatively detects automation and malformed browser environments before
 * analytics or session-replay SDKs are initialized.
 */
export function isBotDetected(
  environment: BrowserEnvironment | null = readBrowserEnvironment(),
): boolean {
  if (!environment) return true;

  const ua = environment.userAgent;
  const chromeMajor = Number.parseInt(ua.match(/Chrome\/(\d+)/)?.[1] || "0");

  if (isbot(ua)) return true;
  if (environment.webdriver) return true;

  if (
    ua.includes("HeadlessChrome") ||
    ua.includes("PhantomJS") ||
    (!environment.hasChromeRuntime &&
      ua.includes("Chrome") &&
      !ua.includes("Edg") &&
      chromeMajor >= 60)
  ) {
    return true;
  }

  if (
    (!environment.hasPermissionsApi &&
      ua.includes("Chrome") &&
      chromeMajor >= 46) ||
    !Array.isArray(environment.languages) ||
    environment.languages.length === 0
  ) {
    return true;
  }

  const automationSignatures = [
    "selenium",
    "webdriver",
    "puppeteer",
    "playwright",
    "cypress",
    "testcafe",
    "phantom",
    "headless",
  ];
  const uaLower = ua.toLowerCase();
  if (automationSignatures.some((signature) => uaLower.includes(signature))) {
    return true;
  }

  if (
    !environment.hasConnectionApi &&
    ua.includes("Chrome") &&
    chromeMajor >= 61
  ) {
    return true;
  }

  if (
    !environment.hasOuterHeight ||
    !environment.hasOuterWidth ||
    !environment.hasInnerHeight ||
    !environment.hasInnerWidth
  ) {
    return true;
  }

  if (ua.length < 10 || ua === "Mozilla/5.0" || !ua.includes("Mozilla")) {
    return true;
  }

  return false;
}
